import React from 'react';
import WindowType from '../../controls/frame/WindowType';
import WindowShape from '../../controls/frame/WindowShape';
import WindowWidth from '../../controls/frame/WinWidth';
import WinHinges from '../../controls/frame/WinHinges';
import WinSliderOprion from '../../controls/frame/WinSliderOption';
import WinTiltAndTurnOption from '../../controls/frame/WinTiltAndTurnOption';
import GrillType from '../../controls/grill/GrillType';
import GrillSize from '../../controls/grill/GrillSize';
import GrillPattern from '../../controls/grill/GrillPattern';
import HorizontalGrills from '../../controls/grill/HorizontalGrills';
import VerticalGrills from '../../controls/grill/VerticalGrills';
import { formatToDecimal } from '../../../controls/conversions';
import { minmaxWindow } from '../../../lib/minmax';
import { USE_SHAPES_TOP, SHAPES_TOP_VALIDATION_ACTIVE } from '../../../constants';

// Helper function to check if a shape value ends with "Left" or "Right"
const endsWithLeft = (value) => value?.endsWith(' Left') || value?.endsWith('Left');
const endsWithRight = (value) => value?.endsWith(' Right') || value?.endsWith('Right');

// Helper function to get the base shape name (without Left/Right)
const getBaseShapeName = (value) => {
  if (!value) return null;
  return value.replace(/ Left$/, '').replace(/ Right$/, '').replace(/Left$/, '').replace(/Right$/, '');
};

// Helper function to calculate disabled options for WindowShape
const getDisabledShapeOptions = (position, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues) => {
  const disabledOptions = [];
  if (SHAPES_TOP_VALIDATION_ACTIVE === false) {
    return disabledOptions;
  }

  // Only apply rules if topRowConfValue is '1' or 'Grid'
  if (!['1', 'Grid'].includes(topRowConfValue)) {
    return disabledOptions;
  }

  // Only apply rules if there's more than 1 shape in the row
  if (mainRowConfValue < 2) {
    return disabledOptions;
  }

  // Get all shape values BEFORE the current position, but ONLY consider shapes that are visible (WindowType is "Picture")
  const shapesBefore = [];
  for (let i = 0; i < position - 1; i++) {
    if (windowTypeValues[i] === 'Picture') {
      shapesBefore.push(shapeValues[i]);
    } else {
      shapesBefore.push(null); // Keep the position but mark as not visible
    }
  }

  // Also get ALL shape values (to check if a Right exists anywhere for position 1)
  const allShapes = [];
  for (let i = 0; i < mainRowConfValue; i++) {
    if (windowTypeValues[i] === 'Picture') {
      allShapes.push(shapeValues[i]);
    } else {
      allShapes.push(null);
    }
  }

  // Find if we have a "Left" or "Right" shape BEFORE current position
  const leftShapeBefore = shapesBefore.find(endsWithLeft);
  const rightShapeBefore = shapesBefore.find(endsWithRight);
  const hasLeftBefore = !!leftShapeBefore;
  const hasRightBefore = !!rightShapeBefore;

  // Find if we have a "Right" shape ANYWHERE (for position 1 validation)
  const rightShapeAnywhere = allShapes.find(endsWithRight);
  const hasRightAnywhere = !!rightShapeAnywhere;

  // Find position of "Right" shape (1-indexed)
  const rightPosition = shapesBefore.findIndex(endsWithRight) + 1;

  // NEW RULE: Left & Right work like simple parentheses (no nesting, must match)
  // If a Left shape exists BEFORE, disable all Right shapes except the matching one
  if (hasLeftBefore) {
    const baseShapeName = getBaseShapeName(leftShapeBefore);
    const allRightShapes = ['Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right'];
    const matchingRightShape = `${baseShapeName} Right`;

    // Disable all right shapes except the matching one
    allRightShapes.forEach(shape => {
      if (shape !== matchingRightShape) {
        disabledOptions.push(shape);
      }
    });
  }

  // If a Right shape exists ANYWHERE, disable all Left shapes except the matching one
  // This handles position 1 when Right is at position 2+
  if (hasRightAnywhere && position === 1) {
    const baseShapeName = getBaseShapeName(rightShapeAnywhere);
    const allLeftShapes = ['Triangle Left', 'Trapezoid Left', 'AngledTopTrapezoid Left', 'HalfEyebrow Left', 'Eyebrow Left'];
    const matchingLeftShape = `${baseShapeName} Left`;

    // Disable all left shapes except the matching one
    allLeftShapes.forEach(shape => {
      if (shape !== matchingLeftShape) {
        disabledOptions.push(shape);
      }
    });
  }

  // Rule: Only one "Left" allowed in the row (must be at position 1)
  // Rule: "Right" shapes can only appear once, and need a matching "Left" at position 1
  if (position === 1) {
    // Position 1: "Left" shapes allowed, but "Right" shapes NOT allowed (no matching "Left" before them)
    disabledOptions.push('Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right');
  } else {
    // Position 2+: disable "Left" options (they can only be at position 1)
    disabledOptions.push('Triangle Left', 'Trapezoid Left', 'AngledTopTrapezoid Left', 'HalfEyebrow Left', 'Eyebrow Left');

    // If we already have a "Right" shape before current position, disable all "Right" options (only one "Right" allowed)
    if (hasRightBefore) {
      disabledOptions.push('Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right');
    }

    // No shapes allowed after "Right" position
    if (rightPosition > 0 && position > rightPosition) {
      // Disable all options (this position shouldn't exist)
      disabledOptions.push('Rectangle', 'Triangle Left', 'Triangle Right', 'Triangle Center',
        'Trapezoid Left', 'Trapezoid Right', 'AngledTopTrapezoid Left', 'AngledTopTrapezoid Right',
        'Pentagon', 'Octagon', 'HalfEyebrow Left', 'HalfEyebrow Right', 'HalfEyebrow Center',
        'Eyebrow Center', 'Eyebrow Left', 'Eyebrow Right', 'Oculus');
    }
  }

  return disabledOptions;
};

export const getSizingAndFrameTopSection = (props) => {
  const {
    isDisabled,
    mainRowConfValue,
    topRowConfValue,
    archGrillTypeValue1, setArchGrillTypeValue1,
    archGrillSizeValue1, setArchGrillSizeValue1,
    archGrillPatternValue1, setArchGrillPatternValue1,
    archRaysGrillsValue1, setRaysGrillsValue1,
    archHorizontalGrillsValue1, setArchHorizontalGrillsValue1,
    archVerticalGrillsValue1, setArchVerticalGrillsValue1,
    
    windowTypeTopValue1, setWindowTypeTopValue1,
    windowTypeTopValue2, setWindowTypeTopValue2,
    windowTypeTopValue3, setWindowTypeTopValue3,
    windowTypeTopValue4, setWindowTypeTopValue4,
    windowTypeTopValue5, setWindowTypeTopValue5,

    windowShapeTopValue1, setWindowShapeTopValue1,
    windowShapeTopValue2, setWindowShapeTopValue2,
    windowShapeTopValue3, setWindowShapeTopValue3,
    windowShapeTopValue4, setWindowShapeTopValue4,
    windowShapeTopValue5, setWindowShapeTopValue5,

    windowWidthValue1,
    windowWidthValue2,
    windowWidthValue3,
    windowWidthValue4,
    windowWidthValue5,

    topRowHeightValue,

    //->
    trapezoidTopLeftSizeValue1, setTrapezoidTopLeftSizeValue1,
    trapezoidTopLeftSizeValue2, setTrapezoidTopLeftSizeValue2,
    trapezoidTopLeftSizeValue3, setTrapezoidTopLeftSizeValue3,
    trapezoidTopLeftSizeValue4, setTrapezoidTopLeftSizeValue4,
    trapezoidTopLeftSizeValue5, setTrapezoidTopLeftSizeValue5,

    trapezoidTopRightSizeValue1, setTrapezoidTopRightSizeValue1,
    trapezoidTopRightSizeValue2, setTrapezoidTopRightSizeValue2,
    trapezoidTopRightSizeValue3, setTrapezoidTopRightSizeValue3,
    trapezoidTopRightSizeValue4, setTrapezoidTopRightSizeValue4,
    trapezoidTopRightSizeValue5, setTrapezoidTopRightSizeValue5,   

    angledTopTrapezoidTopLeftSizeValue1, setAngledTopTrapezoidTopLeftSizeValue1,
    angledTopTrapezoidTopLeftSizeValue2, setAngledTopTrapezoidTopLeftSizeValue2,
    angledTopTrapezoidTopLeftSizeValue3, setAngledTopTrapezoidTopLeftSizeValue3,
    angledTopTrapezoidTopLeftSizeValue4, setAngledTopTrapezoidTopLeftSizeValue4,
    angledTopTrapezoidTopLeftSizeValue5, setAngledTopTrapezoidTopLeftSizeValue5,

    angledTopTrapezoidTopRightSizeValue1, setAngledTopTrapezoidTopRightSizeValue1,
    angledTopTrapezoidTopRightSizeValue2, setAngledTopTrapezoidTopRightSizeValue2,
    angledTopTrapezoidTopRightSizeValue3, setAngledTopTrapezoidTopRightSizeValue3,
    angledTopTrapezoidTopRightSizeValue4, setAngledTopTrapezoidTopRightSizeValue4,
    angledTopTrapezoidTopRightSizeValue5, setAngledTopTrapezoidTopRightSizeValue5,

    //->
    hingesTopValue1, setHingesTopValue1,
    hingesTopValue2, setHingesTopValue2,
    hingesTopValue3, setHingesTopValue3,
    hingesTopValue4, setHingesTopValue4,
    hingesTopValue5, setHingesTopValue5,
    sliderTopValue1, setSliderTopValue1,
    sliderTopValue2, setSliderTopValue2,
    sliderTopValue3, setSliderTopValue3,
    sliderTopValue4, setSliderTopValue4,
    sliderTopValue5, setSliderTopValue5,
    tiltAndTurnTopValue1, setTiltAndTurnTopValue1,
    tiltAndTurnTopValue2, setTiltAndTurnTopValue2,
    tiltAndTurnTopValue3, setTiltAndTurnTopValue3,
    tiltAndTurnTopValue4, setTiltAndTurnTopValue4,
    tiltAndTurnTopValue5, setTiltAndTurnTopValue5,

    radiusxTopValue1, setRadiusxTopValue1,
    radiusxTopValue2, setRadiusxTopValue2,
    radiusxTopValue3, setRadiusxTopValue3,
    radiusxTopValue4, setRadiusxTopValue4,
    radiusxTopValue5, setRadiusxTopValue5,

    radiusyTopValue1, setRadiusyTopValue1,
    radiusyTopValue2, setRadiusyTopValue2,
    radiusyTopValue3, setRadiusyTopValue3,
    radiusyTopValue4, setRadiusyTopValue4,
    radiusyTopValue5, setRadiusyTopValue5,

  } = props;

  // Collect all shape values and window type values for validation
  const shapeValues = [
    windowShapeTopValue1,
    windowShapeTopValue2,
    windowShapeTopValue3,
    windowShapeTopValue4,
    windowShapeTopValue5
  ];

  const windowTypeValues = [
    windowTypeTopValue1,
    windowTypeTopValue2,
    windowTypeTopValue3,
    windowTypeTopValue4,
    windowTypeTopValue5
  ];

  return [
    // Arch starts...
    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) && (
    { id: "ArchGrillType1",  component: 1 > mainRowConfValue ? null : ( <GrillType
      disabled={isDisabled} id = {"ArchGrillType"} label={"Arch Grill Type 1"}
      value={archGrillTypeValue1}  onChange={(newValue) => { setArchGrillTypeValue1(newValue);}
    }/>),   position: [1, 1], status: "active", expandableSectionId: "SizingAndFrameTop" }),

    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) && !['None', 'No Grills'].includes(archGrillTypeValue1?.value) &&
    { id: "ArchGrillSize1",  component: 1 > mainRowConfValue ? null : ( <GrillSize
      disabled={isDisabled} id = {"ArchGrillSize"} label={"Grill Size"}
      grillTypeValue={archGrillTypeValue1?.value}
      value={archGrillSizeValue1}  onChange={(newValue) => { setArchGrillSizeValue1(newValue);}
    }/>),   position: [1, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) && !['None', 'No Grills'].includes(archGrillTypeValue1?.value) &&
    { id: "ArchGrillPattern1",  component: 1 > mainRowConfValue ? null : ( <GrillPattern
      disabled={isDisabled} id = {"ArchGrillPattern"} label={"Grill Pattern"}
      hideOptions = {['Prairie', 'Short Fractional', 'Tall Fractional', 'Queen Anne', 'Victorian']}
      grillTypeValue={archGrillTypeValue1?.value}
      grillSizeValue={archGrillSizeValue1}
      value={archGrillPatternValue1}  onChange={(newValue) => { setArchGrillPatternValue1(newValue);}
    }/>),   position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) &&
    !['None', 'No Grills'].includes(archGrillTypeValue1?.value) && ['Rays'].includes(archGrillPatternValue1?.value) &&
    { id: "ArchRaysGrills1",  component: 1 > mainRowConfValue ? null : ( <HorizontalGrills
      disabled={isDisabled} id = {"ArchRaysGrills1"} label={"Number of rays"}
      value={archRaysGrillsValue1}  onChange={(newValue) => { setRaysGrillsValue1(newValue);}
    }/>),   position: [2, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) &&
    !['None', 'No Grills'].includes(archGrillTypeValue1?.value) && ['Colonial'].includes(archGrillPatternValue1?.value) &&
    { id: "ArchHorizontalGrills1",  component: 1 > mainRowConfValue ? null : ( <HorizontalGrills
      disabled={isDisabled} id = {"ArchHorizontalGrills1"} label={"Horizontal lines"}
      value={archHorizontalGrillsValue1}  onChange={(newValue) => { setArchHorizontalGrillsValue1(newValue);}
    }/>),   position: [2, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['ArchBar', 'Arch', 'ArchGrid11'].includes(topRowConfValue) &&
    !['None', 'No Grills'].includes(archGrillTypeValue1?.value) && ['Colonial'].includes(archGrillPatternValue1?.value) &&
    { id: "ArchVerticalGrills1",  component: 1 > mainRowConfValue ? null : ( <VerticalGrills
      disabled={isDisabled} id = {"ArchVerticalGrills1"} label={"Vertical lines"}
      value={archVerticalGrillsValue1}  onChange={(newValue) => { setArchVerticalGrillsValue1(newValue);}
    }/>),   position: [2, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },
    // Arch ends...

    // SizingAndFrameTop starts...
    ['1', 'Grid'].includes(topRowConfValue) &&
    { id: "WindowTypeTop1",    component: 1 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowTypeTop1"} label={"1. Type"} showPics={true}
        value={windowTypeTopValue1}    onChange={(newValue) => { setWindowTypeTopValue1(newValue);}
    }/>), position: [1, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) &&
    { id: "WindowTypeTop2",    component: 2 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowTypeTop2"} label={"2. Type"} showPics={true}
        value={windowTypeTopValue2} onChange={(newValue) => { setWindowTypeTopValue2(newValue); }
    }/>), position: [3, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) &&
    { id: "WindowTypeTop3",    component: 3 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowTypeTop3"} label={"3. Type"} showPics={true}
        value={windowTypeTopValue3}  onChange={(newValue) => { setWindowTypeTopValue3(newValue); }
    }/>), position: [5, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) &&
    { id: "WindowTypeTop4",    component: 4 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowTypeTop4"} label={"4. Type"} showPics={true}
        value={windowTypeTopValue4}  onChange={(newValue) => { setWindowTypeTopValue4(newValue); }
    }/>), position: [7, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) &&
    { id: "WindowTypeTop5",    component: 5 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowTypeTop5"} label={"5. Type"} showPics={true}
        value={windowTypeTopValue5} onChange={(newValue) => { setWindowTypeTopValue5(newValue); }
    }/>), position: [9, 1], status: "active", expandableSectionId: "SizingAndFrameTop" },

    // WindowShape controls will go here in column 2 (only visible when WindowType is "Picture")
    USE_SHAPES_TOP &&
    ['1', 'Grid'].includes(topRowConfValue) && windowTypeTopValue1 === 'Picture' &&
    { id: "WindowShapeTop1",    component: 1 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShapeTop1"} label={"1. Shape"} showPics={true}
        value={windowShapeTopValue1} onChange={(newValue) => { setWindowShapeTopValue1(newValue); }}
        disabledOptions={getDisabledShapeOptions(1, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [1, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    USE_SHAPES_TOP &&
    ['11', 'Grid'].includes(topRowConfValue) && windowTypeTopValue2 === 'Picture' &&
    { id: "WindowShapeTop2",    component: 2 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShapeTop2"} label={"2. Shape"} showPics={true}
        value={windowShapeTopValue2} onChange={(newValue) => { setWindowShapeTopValue2(newValue); }}
        disabledOptions={getDisabledShapeOptions(2, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [3, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    USE_SHAPES_TOP &&
    ['11', 'Grid'].includes(topRowConfValue) && windowTypeTopValue3 === 'Picture' &&
    { id: "WindowShapeTop3",    component: 3 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShapeTop3"} label={"3. Shape"} showPics={true}
        value={windowShapeTopValue3} onChange={(newValue) => { setWindowShapeTopValue3(newValue); }}
        disabledOptions={getDisabledShapeOptions(3, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [5, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    USE_SHAPES_TOP &&
    ['11', 'Grid'].includes(topRowConfValue) && windowTypeTopValue4 === 'Picture' &&
    { id: "WindowShapeTop4",    component: 4 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShapeTop4"} label={"4. Shape"} showPics={true}
        value={windowShapeTopValue4} onChange={(newValue) => { setWindowShapeTopValue4(newValue); }}
        disabledOptions={getDisabledShapeOptions(4, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [7, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    USE_SHAPES_TOP &&
    ['11', 'Grid'].includes(topRowConfValue) && windowTypeTopValue5 === 'Picture' &&
    { id: "WindowShapeTop5",    component: 5 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShapeTop5"} label={"5. Shape"} showPics={true}
        value={windowShapeTopValue5} onChange={(newValue) => { setWindowShapeTopValue5(newValue); }}
        disabledOptions={getDisabledShapeOptions(5, topRowConfValue, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [9, 2], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['1', 'Grid'].includes(topRowConfValue) &&
    ['Casement', 'Casement2'].includes(windowTypeTopValue1) && { id: "WinHingesTop1",
      component: 1 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHingesTop1" label="Hinges" value={hingesTopValue1}
          onChange={(newValue) => setHingesTopValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Casement', 'Casement2'].includes(windowTypeTopValue2) && { id: "WinHingesTop2",
      component: 2 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHingesTop2" label="Hinges" value={hingesTopValue2}
          onChange={(newValue) => setHingesTopValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Casement', 'Casement2'].includes(windowTypeTopValue3) && { id: "WinHingesTop3",
      component: 3 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHingesTop3" label="Hinges" value={hingesTopValue3}
          onChange={(newValue) => setHingesTopValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Casement', 'Casement2'].includes(windowTypeTopValue4) && { id: "WinHingesTop4",
      component: 4 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHingesTop4" label="Hinges" value={hingesTopValue4}
          onChange={(newValue) => setHingesTopValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Casement', 'Casement2'].includes(windowTypeTopValue5) && { id: "WinHingesTop5",
      component: 5 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHingesTop5" label="Hinges" value={hingesTopValue5}
          onChange={(newValue) => setHingesTopValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },

    ['1', 'Grid'].includes(topRowConfValue) &&
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeTopValue1) && { id: "WinSliderOprionTop1",
      component: 1 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprionTop1" label="Slider Option" value={sliderTopValue1}
          onChange={(newValue) => setSliderTopValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeTopValue2) && { id: "WinSliderOprionTop2",
      component: 2 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprionTop2" label="Slider Option" value={sliderTopValue2}
          onChange={(newValue) => setSliderTopValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeTopValue3) && { id: "WinSliderOprionTop3",
      component: 3 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprionTop3" label="Slider Option" value={sliderTopValue3}
          onChange={(newValue) => setSliderTopValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeTopValue4) && { id: "WinSliderOprionTop4",
      component: 4 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprionTop4" label="Slider Option" value={sliderTopValue4}
          onChange={(newValue) => setSliderTopValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeTopValue5) && { id: "WinSliderOprionTop5",
      component: 5 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprionTop5" label="Slider Option" value={sliderTopValue5}
          onChange={(newValue) => setSliderTopValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },

    ['1', 'Grid'].includes(topRowConfValue) &&
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeTopValue1) &&
    { id: "WinTiltAndTurnOptionTop1",
      component: 1 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOptionTop1" label="Tilt & Turn Option" value={tiltAndTurnTopValue1}
          onChange={(newValue) => setTiltAndTurnTopValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeTopValue2) &&
    { id: "WinTiltAndTurnOptionTop2",
      component: 2 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOptionTop2" label="Tilt & Turn Option" value={tiltAndTurnTopValue2}
          onChange={(newValue) => setTiltAndTurnTopValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeTopValue3) &&
    { id: "WinTiltAndTurnOptionTop3",
      component: 3 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOptionTop3" label="Tilt & Turn Option" value={tiltAndTurnTopValue3}
          onChange={(newValue) => setTiltAndTurnTopValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeTopValue4) &&
    { id: "WinTiltAndTurnOptionTop4",
      component: 4 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOptionTop4" label="Tilt & Turn Option" value={tiltAndTurnTopValue4}
          onChange={(newValue) => setTiltAndTurnTopValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },
    ['11', 'Grid'].includes(topRowConfValue) &&
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeTopValue5) &&
    { id: "WinTiltAndTurnOptionTop5",
      component: 5 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOptionTop5" label="Tilt & Turn Option" value={tiltAndTurnTopValue5}
          onChange={(newValue) => setTiltAndTurnTopValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop",
    },

    //Trapezoid Left & Trapezoid Right
    ['1', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue1) && ['Trapezoid Left'].includes(windowShapeTopValue1) &&
    { id: "TrapezoidTopLeftSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopLeftSize1"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue1) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue1) - 0.5}
        value={trapezoidTopLeftSizeValue1}    onChange={(newValue) => { setTrapezoidTopLeftSizeValue1(newValue); }
    }/>), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['1', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue1) && ['Trapezoid Right'].includes(windowShapeTopValue1) &&
    { id: "TrapezoidTopRightSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopRightSize1"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue1) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue1) - 0.5}
        value={trapezoidTopRightSizeValue1}    onChange={(newValue) => { setTrapezoidTopRightSizeValue1(newValue); }
    }/>), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue2) && ['Trapezoid Left'].includes(windowShapeTopValue2) &&
    { id: "TrapezoidTopLeftSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopLeftSize2"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue2) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue2) - 0.5}
        value={trapezoidTopLeftSizeValue2}    onChange={(newValue) => { setTrapezoidTopLeftSizeValue2(newValue); }
    }/>), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue2) && ['Trapezoid Right'].includes(windowShapeTopValue2) &&
    { id: "TrapezoidTopRightSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopRightSize2"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue2) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue2) - 0.5}
        value={trapezoidTopRightSizeValue2}    onChange={(newValue) => { setTrapezoidTopRightSizeValue2(newValue); }
    }/>), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue3) && ['Trapezoid Left'].includes(windowShapeTopValue3) &&
    { id: "TrapezoidTopLeftSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopLeftSize3"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue3) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue3) - 0.5}
        value={trapezoidTopLeftSizeValue3}    onChange={(newValue) => { setTrapezoidTopLeftSizeValue3(newValue); }
    }/>), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue3) && ['Trapezoid Right'].includes(windowShapeTopValue3) &&
    { id: "TrapezoidTopRightSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopRightSize3"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue3) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue3) - 0.5}
        value={trapezoidTopRightSizeValue3}    onChange={(newValue) => { setTrapezoidTopRightSizeValue3(newValue); }
    }/>), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue4) && ['Trapezoid Left'].includes(windowShapeTopValue4) &&
    { id: "TrapezoidTopLeftSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopLeftSize4"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue4) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue4) - 0.5}
        value={trapezoidTopLeftSizeValue4}    onChange={(newValue) => { setTrapezoidTopLeftSizeValue4(newValue); }
    }/>), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue4) && ['Trapezoid Right'].includes(windowShapeTopValue4) &&
    { id: "TrapezoidTopRightSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopRightSize4"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue4) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue4) - 0.5}
        value={trapezoidTopRightSizeValue4}    onChange={(newValue) => { setTrapezoidTopRightSizeValue4(newValue); }
    }/>), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue5) && ['Trapezoid Left'].includes(windowShapeTopValue5) &&
    { id: "TrapezoidTopLeftSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopLeftSize5"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue5) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue5) - 0.5}
        value={trapezoidTopLeftSizeValue5}    onChange={(newValue) => { setTrapezoidTopLeftSizeValue5(newValue); }
    }/>), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue5) && ['Trapezoid Right'].includes(windowShapeTopValue5) &&
    { id: "TrapezoidTopRightSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidTopRightSize5"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue5) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue5) - 0.5}
        value={trapezoidTopRightSizeValue5}    onChange={(newValue) => { setTrapezoidTopRightSizeValue5(newValue); }
    }/>), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    //AngledTopTrapezoid Left & AngledTopTrapezoid Right
    ['1', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue1) && ['AngledTopTrapezoid Left'].includes(windowShapeTopValue1) &&
    { id: "AngledTopTrapezoidTopLeftSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopLeftSize1"} label={`Left Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopLeftSizeValue1}    onChange={(newValue) => { setAngledTopTrapezoidTopLeftSizeValue1(newValue); }
    }/>), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['1', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue1) && ['AngledTopTrapezoid Right'].includes(windowShapeTopValue1) &&
    { id: "AngledTopTrapezoidTopRightSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopRightSize1"} label={`Right Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopRightSizeValue1}    onChange={(newValue) => { setAngledTopTrapezoidTopRightSizeValue1(newValue); }
    }/>), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue2) && ['AngledTopTrapezoid Left'].includes(windowShapeTopValue2) &&
    { id: "AngledTopTrapezoidTopLeftSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopLeftSize2"} label={`Left Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopLeftSizeValue2}    onChange={(newValue) => { setAngledTopTrapezoidTopLeftSizeValue2(newValue); }
    }/>), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue2) && ['AngledTopTrapezoid Right'].includes(windowShapeTopValue2) &&
    { id: "AngledTopTrapezoidTopRightSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopRightSize2"} label={`Right Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopRightSizeValue2}    onChange={(newValue) => { setAngledTopTrapezoidTopRightSizeValue2(newValue); }
    }/>), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue3) && ['AngledTopTrapezoid Left'].includes(windowShapeTopValue3) &&
    { id: "AngledTopTrapezoidTopLeftSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopLeftSize3"} label={`Left Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopLeftSizeValue3}    onChange={(newValue) => { setAngledTopTrapezoidTopLeftSizeValue3(newValue); }
    }/>), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue3) && ['AngledTopTrapezoid Right'].includes(windowShapeTopValue3) &&
    { id: "AngledTopTrapezoidTopRightSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopRightSize3"} label={`Right Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopRightSizeValue3}    onChange={(newValue) => { setAngledTopTrapezoidTopRightSizeValue3(newValue); }
    }/>), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue4) && ['AngledTopTrapezoid Left'].includes(windowShapeTopValue4) &&
    { id: "AngledTopTrapezoidTopLeftSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopLeftSize4"} label={`Left Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopLeftSizeValue4}    onChange={(newValue) => { setAngledTopTrapezoidTopLeftSizeValue4(newValue); }
    }/>), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue4) && ['AngledTopTrapezoid Right'].includes(windowShapeTopValue4) &&
    { id: "AngledTopTrapezoidTopRightSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopRightSize4"} label={`Right Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopRightSizeValue4}    onChange={(newValue) => { setAngledTopTrapezoidTopRightSizeValue4(newValue); }
    }/>), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },

    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue5) && ['AngledTopTrapezoid Left'].includes(windowShapeTopValue5) &&
    { id: "AngledTopTrapezoidTopLeftSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopLeftSize5"} label={`Left Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopLeftSizeValue5}    onChange={(newValue) => { setAngledTopTrapezoidTopLeftSizeValue5(newValue); }
    }/>), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },
    ['11', 'Grid'].includes(topRowConfValue) && ['Picture'].includes(windowTypeTopValue5) && ['AngledTopTrapezoid Right'].includes(windowShapeTopValue5) &&
    { id: "AngledTopTrapezoidTopRightSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidTopRightSize5"} label={`Right Top Offset 0.5-${formatToDecimal(topRowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(topRowHeightValue) - 0.5}
        value={angledTopTrapezoidTopRightSizeValue5}    onChange={(newValue) => { setAngledTopTrapezoidTopRightSizeValue5(newValue); }
    }/>), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrameTop" },


    // SizingAndFrameTop ends...
  ];
};
